import {Store} from "@ngrx/store";
import {Observable} from "rxjs";
import {distinctUntilChanged, map} from "rxjs/operators";

import {CardCommentContextFactory, ContextDimension, buildScopedContext} from "../../shared/comments/card-context-menu/card-comment-context";
import {selectFundContext} from "../store/fund/fund.selectors";

/**
 * Scope name → the id dimensions (beyond the `fund_id` anchor) that identify a fund card's data, and
 * therefore key its comment thread (Comment-Context-Scoping redesign, spec §3). A card declares one
 * of these scopes via `[commentScope]`; {@link buildScopedContext} emits them in canonical order.
 */
export const FUND_CARD_SCOPE_DIMENSIONS = new Map<string, ContextDimension[]>([
    ["fund", []],
    ["fund-report", ["fund_report_id"]],
    ["fund-report-valuation", ["fund_report_id", "fund_valuation_id"]],
    ["fund-report-valuation-deal-seller", ["fund_report_id", "fund_valuation_id", "deal_id", "seller_position_id"]],
]);

/**
 * Fund-page {@link CardCommentContextFactory}: resolves each card's context from its declared scope
 * and the fund page's live `{fund, report, valuation, deal}` ids ({@link selectFundContext}). A scope
 * whose required id is not yet selected yields `null`, so the card shows no comment menu until the
 * data it comments on is on screen.
 */
export const createFundCardCommentContextFactory = (store: Store): CardCommentContextFactory => {
    const context$ = store.select(selectFundContext);
    return {
        build: (scope: string, slug: string): Observable<string | null> => context$.pipe(
            map(({fundId, fundReportId, fundValuationId, dealId, sellerPositionId}) => {
                const available = new Map<ContextDimension, number | null | undefined>([
                    ["fund_report_id", fundReportId],
                    ["fund_valuation_id", fundValuationId],
                    ["deal_id", dealId],
                    ["seller_position_id", sellerPositionId],
                ]);
                const dims = new Map<ContextDimension, number | null | undefined>();
                for (const dimension of FUND_CARD_SCOPE_DIMENSIONS.get(scope) ?? []) {
                    dims.set(dimension, available.get(dimension));
                }
                return buildScopedContext("fund_id", fundId, dims, slug);
            }),
            distinctUntilChanged(),
        ),
    };
};
